import { ACTIVITY_OPTIONS } from '../../../util/enums';

const PRICING_MODEL_DIFFERENT = 'model1';
const PRICING_MODEL_FIXED = 'model2';
const BOOKING_PURPOSE_TO_ACTIVITY = {
  PHOTO: 'photo',
  VIDEO: 'video',
};

const isPlainObject = value => {
  return !!value && typeof value === 'object' && !Array.isArray(value);
};

export const getPricingModel = listing => {
  return listing?.attributes?.publicData?.advancePricingDetails?.pricingModel || null;
};

export const getActivityKeyForBookingPurpose = bookingPurpose => {
  if (typeof bookingPurpose !== 'string') {
    return '';
  }

  return BOOKING_PURPOSE_TO_ACTIVITY[bookingPurpose.trim().toUpperCase()] || '';
};

export const normalizeActivityKey = value => {
  if (typeof value !== 'string') {
    return '';
  }

  const normalizedValue = value.trim().toLowerCase();
  if (!normalizedValue) {
    return '';
  }

  const matchingActivity = ACTIVITY_OPTIONS.find(option => option.key === normalizedValue);
  return matchingActivity ? matchingActivity.key : '';
};

const hasTextValue = value => typeof value === 'string' && value.trim().length > 0;

export const hasCompleteProjectInformation = projectInformation => {
  if (!isPlainObject(projectInformation)) {
    return false;
  }

  return (
    !!normalizeActivityKey(projectInformation.activity) &&
    hasTextValue(projectInformation.crewSize) &&
    hasTextValue(projectInformation.projectName) &&
    hasTextValue(projectInformation.renterCompany) &&
    hasTextValue(projectInformation.productionCompany) &&
    hasTextValue(projectInformation.aboutProject)
  );
};

export const parseSavedProjectInfo = rawProjectInfo => {
  if (!rawProjectInfo) {
    return {};
  }

  try {
    const parsedProjectInfo = JSON.parse(rawProjectInfo);
    return isPlainObject(parsedProjectInfo) ? parsedProjectInfo : {};
  } catch (error) {
    return {};
  }
};

const getPricedActivityKeys = listing => {
  const pricingMatrices =
    listing?.attributes?.publicData?.advancePricingDetails?.pricingMatrices || {};

  return Object.keys(pricingMatrices)
    .map(getActivityKeyForBookingPurpose)
    .filter(Boolean);
};

export const isFixedPricingListing = listing => getPricingModel(listing) === PRICING_MODEL_FIXED;

export const isDifferentPricingListing = listing =>
  getPricingModel(listing) === PRICING_MODEL_DIFFERENT;

export const getProjectInfoActivityOptions = ({
  listing,
  bookingModalDetails = {},
  selectedActivity,
} = {}) => {
  if (isFixedPricingListing(listing)) {
    return ACTIVITY_OPTIONS;
  }

  const bookedActivity = getActivityKeyForBookingPurpose(bookingModalDetails?.bookingType);

  if (isDifferentPricingListing(listing) && bookedActivity) {
    return ACTIVITY_OPTIONS.filter(option => option.key === bookedActivity);
  }

  if (isDifferentPricingListing(listing)) {
    const pricedActivityKeys = getPricedActivityKeys(listing);
    const selectedActivityKey = normalizeActivityKey(selectedActivity);
    const allowedActivityKeys = selectedActivityKey
      ? pricedActivityKeys.filter(key => key === selectedActivityKey)
      : pricedActivityKeys;

    return ACTIVITY_OPTIONS.filter(option => allowedActivityKeys.includes(option.key));
  }

  return ACTIVITY_OPTIONS;
};

export const isProjectInfoActivityReadOnly = ({ listing, bookingModalDetails = {} } = {}) => {
  return (
    isDifferentPricingListing(listing) &&
    !!getActivityKeyForBookingPurpose(bookingModalDetails?.bookingType)
  );
};

export const sanitizeProjectInfoForBooking = ({
  projectInformation = {},
  listing,
  bookingModalDetails = {},
} = {}) => {
  const normalizedProjectInformation = isPlainObject(projectInformation) ? projectInformation : {};
  const activity = normalizeActivityKey(normalizedProjectInformation.activity);
  const bookedActivity = getActivityKeyForBookingPurpose(bookingModalDetails?.bookingType);

  if (isDifferentPricingListing(listing) && bookedActivity) {
    return {
      ...normalizedProjectInformation,
      activity: bookedActivity,
    };
  }

  return {
    ...normalizedProjectInformation,
    activity,
  };
};

export const getInitialProjectInfoValues = ({
  savedProjectInfo = {},
  bookingModalDetails = {},
  listing,
} = {}) => {
  const normalizedSavedProjectInfo = isPlainObject(savedProjectInfo) ? savedProjectInfo : {};
  const normalizedBookingModalDetails = isPlainObject(bookingModalDetails)
    ? bookingModalDetails
    : {};
  const bookingActivity =
    getActivityKeyForBookingPurpose(normalizedBookingModalDetails.bookingType) ||
    normalizeActivityKey(normalizedBookingModalDetails.bookingType);
  const savedActivity = normalizeActivityKey(normalizedSavedProjectInfo.activity);
  const shouldUseBookedActivity = isDifferentPricingListing(listing) && bookingActivity;

  return {
    ...normalizedSavedProjectInfo,
    activity: shouldUseBookedActivity ? bookingActivity : bookingActivity || savedActivity || '',
    crewSize: normalizedBookingModalDetails.crewSize || normalizedSavedProjectInfo.crewSize || '',
  };
};
